import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/Button";

interface CptCodeSelectorProps {
  cptCodes: string[];
  selectedCptCodes: Set<string>;
  onSelectionChange: (codes: Set<string>) => void;
}

export const CptCodeSelector: React.FC<CptCodeSelectorProps> = ({
  cptCodes,
  selectedCptCodes,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter CPT codes based on search term
  const filteredCptCodes = cptCodes.filter((code) =>
    code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allSelected = selectedCptCodes.size === cptCodes.length && cptCodes.length > 0;

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(cptCodes));
    }
  };

  const handleToggleCode = (code: string) => {
    const newSelection = new Set(selectedCptCodes);
    if (newSelection.has(code)) {
      newSelection.delete(code);
    } else {
      newSelection.add(code);
    }
    onSelectionChange(newSelection);
  };

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center space-x-2 h-10 pr-8 relative"
      >
        <span className="text-sm">
          {selectedCptCodes.size === 0
            ? "All CPT Codes"
            : `CPT Codes (${selectedCptCodes.size}/${cptCodes.length})`}
        </span>
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute right-2 h-4 w-4 text-primary-blue"
          viewBox="0 0 24 24"
          fill="currentColor"
          focusable="false"
        >
          <path d="M7 10l5-5 5 5H7z" />
          <path d="M7 14l5 5 5-5H7z" />
        </svg>
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-72 bg-white shadow-2xl border border-border-gray z-[100] max-h-96 overflow-hidden flex flex-col"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border border-border-gray rounded focus:outline-none focus:ring-2 focus:ring-primary-blue"
            />
          </div>

          {/* Select All */}
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <label className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
                className="h-4 w-4 border-border-gray rounded cursor-pointer accent-primary-blue"
              />
              <span className="ml-3 text-sm font-semibold text-gray-800">Select All</span>
            </label>
          </div>

          {/* Options List */}
          <div className="p-2 overflow-y-auto">
            {filteredCptCodes.length > 0 ? (
              filteredCptCodes.map((code) => (
                <label
                  key={code}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedCptCodes.has(code)}
                    onChange={() => handleToggleCode(code)}
                    className="h-4 w-4 border-border-gray rounded cursor-pointer accent-primary-blue"
                  />
                  <span className="ml-3 text-sm text-gray-700">{code}</span>
                </label>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
